import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import { useWizardFormId, withForm } from '../utils/wizardForm';

function FormSummary8849() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Номер формы — из адреса этой вкладки (см. utils/wizardForm.js)
  const formId = useWizardFormId();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const [vehicles, setVehicles] = useState([]);
  const [taxYear, setTaxYear] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [baseTaxAmount, setBaseTaxAmount] = useState(0);
  const [totalIrsTax, setTotalIrsTax] = useState('');

  const steps = [
    { id: 1, label: 'Tax & Form' },
    { id: 2, label: 'Vehicles Tax' },
    { id: 3, label: 'IRS Payment Options' },
    { id: 4, label: 'Form Summary' },
    { id: 5, label: 'Payment and IRS Submission' },
    { id: 6, label: 'IRS Confirmation' },
  ];
  const currentStep = 4;

  // Аккордеон: какая секция открыта
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (key) => {
    setOpenSection(openSection === key ? null : key);
  };

  // Загрузка компании
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/companies/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        setCompany(await res.json());
      } catch (e) {
        console.error(e);
      }
    };
    fetchCompany();
  }, [id]);

  // Загрузка формы и машин
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!formId) return;

        const res = await fetch(`/api/form8849/${formId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        const data = await res.json();

        setTaxYear(data.tax_year || 'N/A');
        setPaymentMethod(data.irs_payment_option || 'N/A');

        // «заглушечный» расчет
        const commission = 20;
        const sumTax = 1;
        setBaseTaxAmount(sumTax);
        setTotalIrsTax(`$${(sumTax + commission).toFixed(2)}`);

        // детали машин
        const details = await Promise.all(
          (data.vehicles || []).map(async (vid) => {
            const rv = await fetch(`/api/vehicles/${vid}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!rv.ok) throw new Error();
            return rv.json();
          })
        );
        setVehicles(details);

        // обновим шаг
        await fetch(`/api/form8849/${formId}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ step: 4 }),
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [formId]);

  const handleUpdateTotal = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!formId) throw new Error();

      const sumTax = 1;
      const total = sumTax + 20;
      const res = await fetch(`/api/form8849/${formId}/update_info/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ total_tax_due: total, step: 4 }),
      });
      if (!res.ok) throw new Error();

      navigate(withForm(`/company/${id}/file-form/payment-and-irs-submission`, formId));
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <HeaderLogged />

      {/* Навигация по шагам */}
      <div className="bg-white shadow p-4">
        <div className="flex flex-wrap justify-center gap-2">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`px-2 py-1 sm:px-4 sm:py-2 rounded border text-center text-xs sm:text-sm ${
                step.id === currentStep
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-gray-300 text-gray-700 border-gray-300'
              }`}
            >
              {step.label}
            </div>
          ))}
        </div>
      </div>

      <main className="flex-1 bg-[#E4F2FF] p-4 flex justify-center">
        <div className="bg-white rounded shadow p-6 w-full max-w-6xl space-y-6">
          <h1 className="text-2xl font-bold text-[#0F2C5E]">Form Summary</h1>

          <div className="flex flex-col md:flex-row gap-6">
            {/* Левый столбец: таблица машин */}
            <div className="md:w-2/3 space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-[#0F2C5E]">Taxable Vehicles</h2>
                <button
                  className="bg-[#0F2C5E] text-white px-4 py-2 rounded hover:bg-blue-900 transition"
                  onClick={() => alert('Add Vehicle not implemented')}
                >
                  Add Vehicle
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left">VIN</th>
                      <th className="px-4 py-2 text-left">Weight</th>
                      <th className="px-4 py-2 text-left">Tax Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.map((v, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="px-4 py-2">{v.vin}</td>
                        <td className="px-4 py-2">{v.gross_vehicle_weight}</td>
                        <td className="px-4 py-2">${v.taxable_weight}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Правый столбец: аккордеон */}
            <div className="md:w-1/3 bg-gray-50 border rounded divide-y">
              <h2 className="px-4 py-3 text-xl font-semibold text-[#0F2C5E]">
                Total for
              </h2>

              {/* Business Information */}
              <div>
                <button
                  onClick={() => toggleSection('business')}
                  className="w-full flex justify-between items-center px-4 py-3 hover:bg-gray-100"
                >
                  <span className="font-semibold">Business Information</span>
                  <span className="ml-2 font-bold text-gray-600">
                    {openSection === 'business' ? '–' : '+'}
                  </span>
                </button>
                {openSection === 'business' && (
                  <div className="px-4 py-2 text-sm text-gray-700 space-y-2">
                  <div><strong>Business Profile</strong></div>
                  <div>Name: {company.name}</div>
                  <div>EIN: {company.EIN}</div>
                  <div>Type: {company.business_type}</div>

                  {/* <div className="mt-2"><strong>Contact Info</strong></div>
                  <div>Address: {company.street_address}</div>
                  <div>Phone: {company.signing_phone}</div>
                  <div>Email: {company.email}</div> */}

                  <div className="mt-2"><strong>Signing Authority</strong></div>
                  <div>Name: {company.signing_name}</div>
                  <div>Title: {company.signing_job_title}</div>
                  <div>Phone: {company.signing_phone}</div>
                </div>
                )}
              </div>

              {/* Tax Year Information */}
              <div>
                <button
                  onClick={() => toggleSection('taxYear')}
                  className="w-full flex justify-between items-center px-4 py-3 hover:bg-gray-100"
                >
                  <span className="font-semibold">Tax Year Information</span>
                  <span className="ml-2 font-bold text-gray-600">
                    {openSection === 'taxYear' ? '–' : '+'}
                  </span>
                </button>
                {openSection === 'taxYear' && (
                  <div className="px-4 py-2 text-sm text-gray-700">
                    Selected Year: {taxYear}
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div>
                <button
                  onClick={() => toggleSection('payment')}
                  className="w-full flex justify-between items-center px-4 py-3 hover:bg-gray-100"
                >
                  <span className="font-semibold">Payment Method</span>
                  <span className="ml-2 font-bold text-gray-600">
                    {openSection === 'payment' ? '–' : '+'}
                  </span>
                </button>
                {openSection === 'payment' && (
                  <div className="px-4 py-2 text-sm text-gray-700">
                    {paymentMethod}
                  </div>
                )}
              </div>

              {/* Итоги вне аккордеона */}
              <div className="px-4 py-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Form Type:</span>
                  <span>8849</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Total IRS Tax Amount:</span>
                  <span className="font-semibold">{totalIrsTax}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Filing Fee:</span>
                  <span>$80.00</span>
                </div>
              </div>
            </div>
          </div>

          {/* Навигация внизу */}
          <div className="flex flex-col sm:flex-row justify-between mt-4 gap-4">
            <button
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition w-full sm:w-auto"
              onClick={() => navigate(withForm(`/company/${id}/file-form/irs-payment-options-8849`, formId))}
            >
              Back
            </button>
            <button
              className="bg-[#0F2C5E] text-white px-4 py-2 rounded hover:bg-blue-900 transition w-full sm:w-auto"
              onClick={handleUpdateTotal}
            >
              Continue Filing
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default FormSummary8849;
